import { ReactElement, createContext, useEffect, useReducer, useState } from 'react';

// third-party
import firebase from 'firebase/compat/app';
import {
    getAuth,
    getMultiFactorResolver,
    MultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier
} from 'firebase/auth';

// reducer - state management
import { LOGIN, LOGOUT, CODE_VERIFIED } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import { FIREBASE_API, APP_DOMAIN } from 'config';
import { useLocation, useNavigate } from 'react-router-dom';

import { openSnackbar } from 'store/slices/snackbar';
import { dispatch as dispatchState, useSelector } from 'store';
import { useIntl } from 'react-intl';
import { FirebasesContextType, InitialLoginContextProps } from 'types/components/security';
import { getTenantId, logoutState } from 'store/slices/auth';
import { expToken } from 'utils/helpers';

if (!firebase.apps.length) {
    firebase.initializeApp(FIREBASE_API);
}

// constant
const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    isAuthorized: true,
    user: null,
    codeVerified: false
};

type setSessionProps = {
    serviceToken?: string | null;
    user?: string | null;
};

type PropsData = {
    verificationId: string;
    resolver: MultiFactorResolver;
    recaptchaVerifier: RecaptchaVerifier;
};

const setSession = ({ serviceToken, user }: setSessionProps) => {
    if (serviceToken && user) {
        localStorage.setItem('serviceToken', serviceToken);
        localStorage.setItem('user', user);
    } else {
        localStorage.removeItem('serviceToken');
    }
};

// ==============================|| Firebase CONTEXT & PROVIDER ||============================== //

const FirebaseContext = createContext<FirebasesContextType | null>(null);

export const AWSCognitoProvider = ({ children }: { children: ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    const [dataLog, setDataLog] = useState<PropsData>({} as PropsData);
    const [recaptchaCounter, setRecaptchaCounter] = useState(0);
    const [loading, setLoading] = useState(false);

    const { tenantId } = useSelector((state) => state.auth);

    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl();

    useEffect(() => {
        const restoredTenandId = localStorage.getItem('tenantId');

        if (restoredTenandId) {
            firebase.auth().tenantId = restoredTenandId;
        }

        if (!restoredTenandId) {
            dispatchState(getTenantId()).then((resp) => {
                if (resp.payload) {
                    firebase.auth().tenantId = resp.payload as string;
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const serviceToken = window.localStorage.getItem('serviceToken');

        if (serviceToken) {
            const expirationToken = expToken(serviceToken);
            const dateNow = Date.now();

            if (expirationToken <= dateNow) {
                logout();
            }
        } else {
            logout();
        }
    }, [location.pathname]);

    useEffect(() => {
        firebase.auth().onAuthStateChanged(async (user) => {
            if (user && user.emailVerified) {
                // await
                await user.getIdToken(true);
                await user.getIdTokenResult();
                dispatch({
                    type: LOGIN,
                    payload: {
                        isLoggedIn: true,
                        isInitialized: true,
                        user: {
                            id: user.uid,
                            email: user.email || '',
                            name: user.displayName || '',
                            user: user.email || ''
                        }
                    }
                });
            } else {
                dispatch({
                    type: LOGOUT
                });
            }
        });
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = window.localStorage.getItem('serviceToken');
                const userName = window.localStorage.getItem('user') ?? '';
                if (serviceToken) {
                    setSession({ serviceToken, user: userName });
                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user: {
                                user: userName
                            }
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const signInWithPopup = async () => {
        setLoading(true);
        firebase.auth().tenantId = tenantId;
        const samlProvider = new firebase.auth.SAMLAuthProvider(`saml.${APP_DOMAIN}`);
        firebase
            .auth()
            .signInWithPopup(samlProvider)
            .then(async (result) => {
                if (result?.user) {
                    const token = await result.user.getIdToken();
                    setSession({ serviceToken: token, user: result.user.email });
                    return;
                }
                throw new Error('Fallo la autentificación del usuario.');
            })
            .catch((error) => {
                snackbarShow(error.message, 'error');
                setSession({});
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const signInWithGoogle = async () => {
        setLoading(true);
        firebase.auth().tenantId = tenantId;
        const googleProvider = new firebase.auth.GoogleAuthProvider();
        firebase
            .auth()
            .signInWithPopup(googleProvider)
            .then(async (result) => {
                if (result?.user) {
                    const token = await result.user.getIdToken();
                    setSession({ serviceToken: token, user: result.user.email });
                    return;
                }
                throw new Error('Fallo la autentificación del usuario.');
            })
            .catch((error) => {
                snackbarShow(error.message, 'error');
                setSession({});
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const signInWithFacebook = async () => {
        setLoading(true);
        firebase.auth().tenantId = tenantId;
        const facebookProvider = new firebase.auth.FacebookAuthProvider();
        firebase
            .auth()
            .signInWithPopup(facebookProvider)
            .then(async (result) => {
                if (result?.user) {
                    const token = await result.user.getIdToken();
                    setSession({ serviceToken: token, user: result.user.email });
                    return;
                }
                throw new Error('Fallo la autentificación del usuario.');
            })
            .catch((error) => {
                snackbarShow(error.message, 'error');
                setSession({});
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const signInWithMicrosoft = async () => {
        setLoading(true);
        firebase.auth().tenantId = tenantId;
        const microsoftProvider = new firebase.auth.OAuthProvider('microsoft.com');
        firebase
            .auth()
            .signInWithPopup(microsoftProvider)
            .then(async (result) => {
                if (result?.user) {
                    const token = await result.user.getIdToken();
                    setSession({ serviceToken: token, user: result.user.email });
                    return;
                }
                throw new Error('Fallo la autentificación del usuario.');
            })
            .catch((error) => {
                snackbarShow(error.message, 'error');
                setSession({});
            })
            .finally(() => {
                setLoading(false);
            });
    };

    async function login(email: string, password: string) {
        try {
            handleRecaptchaReset();

            firebase.auth().tenantId = tenantId;

            const userResponse = await firebase.auth().signInWithEmailAndPassword(email, password);
            const user = userResponse?.user;

            if (!user) return;

            if (!user.emailVerified) {
                await user.sendEmailVerification();
                snackbarShow('A verification email has been sent. Please verify your email first.', 'success');
                await firebase.auth().signOut();
                return;
            }

            const token = await user.getIdToken();
            setSession({ serviceToken: token, user: user.email });
        } catch (error: any) {
            if (error?.code === 'auth/multi-factor-auth-required') {
                const auth = getAuth();
                const recaptchaVerifier = new RecaptchaVerifier(
                    `recaptcha-container-${recaptchaCounter + 1}`,
                    {
                        size: 'invisible',
                        callback: () => {}
                    },
                    auth
                );
                const resolver = getMultiFactorResolver(auth, error);
                if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
                    const phoneInfoOptions = {
                        multiFactorHint: resolver.hints[0],
                        session: resolver.session
                    };
                    const phoneAuthProvider = new PhoneAuthProvider(auth);
                    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
                    setDataLog({ verificationId, resolver, recaptchaVerifier });
                    dispatch({
                        type: CODE_VERIFIED,
                        payload: {
                            codeVerified: true,
                            isLoggedIn: false
                        }
                    });
                    snackbarShow('A code has been sent to your phone number', 'success');
                    return;
                }
            }
            if (error?.code === 'auth/wrong-password') {
                snackbarShow('Invalid credentials', 'error');
                return;
            }

            snackbarShow('Were sorry something went wrong trying to login', 'error');
        }
    }

    const finishMFA = async (code: string) => {
        try {
            const cred = PhoneAuthProvider.credential(dataLog.verificationId, code);
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
            // Complete sign-in.
            const userCredential = await dataLog.resolver.resolveSignIn(multiFactorAssertion);
            const token = await userCredential.user.getIdToken();
            setSession({ serviceToken: token, user: userCredential.user.email });
        } catch (error: any) {
            try {
                if (error?.code === 'auth/invalid-verification-code') {
                    snackbarShow('Invalid Code', 'error');
                    return;
                }
                snackbarShow('An error occurred while sending the verification code', 'error');
                dataLog.recaptchaVerifier.clear();
                setCodeVerified();
                logout();
            } catch (e) {
                snackbarShow('An error occurred while sending the verification code', 'error');
                setCodeVerified();
                logout();
            }
        }
    };

    const setCodeVerified = () => {
        dispatch({
            type: CODE_VERIFIED,
            payload: {
                codeVerified: false,
                isLoggedIn: false
            }
        });
    };

    const logout = async () => {
        await firebase.auth().signOut();

        dispatch({
            type: LOGOUT
        });

        dispatch(logoutState());

        setSession({});

        document.cookie.split(';').forEach(function (c) {
            document.cookie = c.replace(/^ +/, '').replace(/(.+)=([^;]*)/, '$1=;expires=' + new Date(0).toUTCString() + ';path=/');
        });

        localStorage.clear();

        sessionStorage.clear();
    };

    async function resetPassword(email: string) {
        try {
            firebase.auth().tenantId = tenantId;
            await firebase.auth().sendPasswordResetEmail(email);
            snackbarShow('Email sent, check your email.', 'success');
            navigate({ pathname: '/' });
        } catch (error) {
            snackbarShow('An error occurred while sending the reset email', 'error');
        }
    }

    async function checkOob(oob: string) {
        try {
            firebase.auth().tenantId = tenantId;
            await firebase.auth().verifyPasswordResetCode(oob);
        } catch (error) {
            snackbarShow('Your code is invalid', 'error');
            navigate({ pathname: '/loolu/admin/login' });
        }
    }

    async function resetPasswordByOob(oob: string, newPassword: string) {
        try {
            firebase.auth().tenantId = tenantId;
            await firebase.auth().confirmPasswordReset(oob, newPassword);
            snackbarShow('Your password is changed .', 'success');
            navigate({ pathname: '/loolu/admin/login' });
        } catch (error) {
            snackbarShow('An error occurred ', 'error');
        }
    }

    async function changePasswordFirebase(newPassword: string) {
        const user = firebase.auth().currentUser;

        if (!user) {
            return;
        }

        try {
            await user.updatePassword(newPassword);
            snackbarShow('Password successfully updated', 'success');
        } catch (error: any) {
            if (error?.code === 'auth/requires-recent-login') {
                snackbarShow(`${intl.formatMessage({ id: 'operation_sensitive' })}`, 'error');
                return;
            }
            snackbarShow('An error occurred updating your password', 'error');
        }
    }

    const snackbarShow = (message: string | unknown, variant: 'success' | 'error') => {
        dispatchState(
            openSnackbar({
                message,
                variant
            })
        );
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    function handleRecaptchaReset() {
        setRecaptchaCounter((prevCounter) => prevCounter + 1);
    }

    return (
        <FirebaseContext.Provider
            value={{
                ...state,
                login,
                logout,
                resetPassword,
                changePasswordFirebase,
                finishMFA,
                setCodeVerified,
                recaptchaCounter,
                signInWithPopup,
                loading,
                checkOob,
                resetPasswordByOob,
                signInWithGoogle,
                signInWithFacebook,
                signInWithMicrosoft
            }}
        >
            {children}
        </FirebaseContext.Provider>
    );
};

export default FirebaseContext;

export async function getUpdatedToken(): Promise<string | null> {
    const token = localStorage.getItem('serviceToken');

    if (!token) {
        return null;
    }

    const bufferTime = 10 * 60 * 1000;
    const expirationTime = expToken(token || '');
    const currentTime = Date.now();

    if (token && expirationTime - currentTime > bufferTime) {
        return token;
    }

    const currentUser = firebase.auth().currentUser;

    if (!currentUser) {
        return null;
    }

    try {
        const newToken = await currentUser.getIdToken(true);

        localStorage.setItem('serviceToken', newToken);
        return newToken;
    } catch (error) {
        return null;
    }
}
